import Axios from "axios";

const CWEstimatePrinter = (receiptData, estimateId, documentNo) => {
  const storedTokenData = localStorage.getItem("tokenData") ? JSON.parse(localStorage.getItem("tokenData")) : {};
  const lastDate = storedTokenData.date || "";
  const tillData = JSON.parse(localStorage.getItem("tillData"));

  const processTokenTemplate = (template, data) => {
    const processedTemplate = JSON.parse(JSON.stringify(template));
    processedTemplate.elements = processedTemplate.elements.flatMap((element) => {
      if (element.type === "conditional" && element.condition && element.elements) {
        const conditionResult = evaluateCondition(element.condition, data);
        if (conditionResult) {
          return element.elements.flatMap((nestedElement) => processTokenTemplate({ elements: [nestedElement] }, data).elements);
        }
        return [];
      }
      if (element.type === "text" && element.content && typeof element.content === "string") {
        element.content = replacePlaceholders(element.content, data);
      }
      if ((element.type === "barcode" || element.type === "qrcode") && element.content) {
        element.content = replacePlaceholders(element.content, data);
      }
      // Process table elements
      if (element.type === "table" && element.data && typeof element.data === "string" && element.dataMapping) {
        const dataPath = element.data.replace(/{{|}}/g, ""); // e.g., "items"
        const tableData = getDeepValue(data, dataPath) || [];

        const columnKeys = element.columns.map((col, idx) => ({
          key: Object.keys(element.dataMapping).find((k) => element.dataMapping[k] === Object.values(element.dataMapping)[idx]),
          width: col.width,
          alignment: col.alignment,
          format: col.format,
          trim: col?.trim === true,
        }));

        element.data = (Array.isArray(tableData) ? tableData : [])
          .flatMap((item) => {
            // Skip main row for items with children (handled by GroupedItems)
            const hasChildren = item[element.GroupedItems?.field] && Array.isArray(item[element.GroupedItems?.field]) && item[element.GroupedItems?.field].length > 0;
            const mainRow = !hasChildren
              ? {
                ...Object.keys(element.dataMapping).reduce((row, targetKey) => {
                  const colMeta = columnKeys.find((ck) => ck.key === targetKey);
                  row[targetKey] = formatValue(item[element.dataMapping[targetKey]], colMeta?.format, colMeta);
                  return row;
                }, {}),
              }
              : null;

            // Extra rows based on extraRows configuration
            const extraRows = (element.extraRows || []).flatMap((extra) => {
              // Handle fields array (e.g., discount)
              if (extra.fields && Array.isArray(extra.fields)) {
                const allHidden = extra.fields.every((fieldConfig) => {
                  const value = item[fieldConfig.field];
                  return fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
                });

                if (allHidden) {
                  return []; // Skip if all fields are hidden
                }

                const extraRow = {};
                let hasContent = false;
                columnKeys.forEach((col) => {
                  const fieldConfig = extra.fields.find((f) => f.column === col.key);
                  if (fieldConfig) {
                    const value = item[fieldConfig.field];
                    const shouldHide = fieldConfig.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
                    if (!shouldHide && value !== null && value !== undefined) {
                      extraRow[col.key] = (fieldConfig.prefix || "") + (fieldConfig.format || fieldConfig.trim ? formatValue(value, fieldConfig?.format, fieldConfig) : value);
                      hasContent = true;
                    } else {
                      extraRow[col.key] = "";
                    }
                  } else {
                    extraRow[col.key] = "";
                  }
                });

                return hasContent ? [extraRow] : [];
              }

              // Handle single field (e.g., addons with loop or name2)
              const value = item[extra.field];
              if (extra.loop) {
                const loopData = value || [];
                if (!Array.isArray(loopData)) {
                  return []; // Skip if not an array
                }

                return loopData.flatMap((loopItem) => {
                  const subValue = loopItem[extra.subField];
                  const shouldHide = extra.hideIfZeroOrEmpty && (subValue === null || subValue === undefined || subValue === "");
                  if (!shouldHide && subValue !== null && subValue !== undefined) {
                    const extraRow = {};
                    columnKeys.forEach((col) => {
                      extraRow[col.key] = col.key === extra.column ? (extra.prefix || "") + subValue : "";
                      if ((extra.format || extra.trim) && col.key === extra.column) {
                        extraRow[col.key] = formatValue(subValue, extra?.format, extra);
                      }
                    });
                    return [extraRow];
                  }
                  return [];
                });
              }

              // Handle non-looped single field
              const shouldHide = extra.hideIfZeroOrEmpty && (value === null || value === undefined || value === 0);
              if (!shouldHide && value !== null && value !== undefined) {
                const extraRow = {};
                columnKeys.forEach((col) => {
                  extraRow[col.key] = col.key === extra.column ? value : "";
                  if ((extra.format || extra.trim) && col.key === extra.column) {
                    extraRow[col.key] = formatValue(value, extra?.format, extra);
                  }
                });
                return [extraRow];
              }
              return [];
            });

            // Process GroupedItems
            const groupedRows = [];
            if (element.GroupedItems) {
              const groupedConfig = element.GroupedItems;
              const value = item[groupedConfig.field];
              if (groupedConfig.loop && Array.isArray(value) && value.length > 0) {
                // Add bold parent row if specified
                if (groupedConfig.boldParent && item[groupedConfig.subField]) {
                  const parentRow = {};
                  columnKeys.forEach((col) => {
                    parentRow[col.key] = col.key === Object.keys(element.dataMapping)[0] ? item[groupedConfig.subField] : "";
                  });
                  parentRow.style = ["bold"];
                  groupedRows.push(parentRow);
                }

                // Add child rows
                value.forEach((loopItem) => {
                  const subValue = loopItem[groupedConfig.subField];
                  const shouldHide = groupedConfig.hideIfZeroOrEmpty && (subValue === null || subValue === undefined || subValue === "");
                  if (!shouldHide && subValue !== null && subValue !== undefined) {
                    const groupedRow = {};
                    columnKeys.forEach((col) => {
                      if (groupedConfig.dataMapping && groupedConfig.dataMapping[col.key]) {
                        groupedRow[col.key] = formatValue(
                          loopItem[groupedConfig.dataMapping[col.key]],
                          col.format || (groupedConfig.format && col.key === groupedConfig.column ? groupedConfig.format : undefined),
                          col
                        );
                      } else {
                        groupedRow[col.key] = col.key === groupedConfig.column ? (groupedConfig.prefix || "") + subValue : "";
                      }
                    });
                    groupedRows.push(groupedRow);
                  }
                });
              }
            }

            return mainRow ? [mainRow, ...extraRows, ...groupedRows] : [...extraRows, ...groupedRows];
          })
          .filter((row) => {
            // Final filter to remove any fully empty rows
            return Object.values(row).some((val) => val !== "" && val !== null && val !== undefined);
          });
      }
      if (element.type === "loop" && element.data && typeof element.data === "string") {
        const loopDataPath = element.data.replace(/{{|}}/g, "");
        const loopData = getDeepValue(data, loopDataPath) || [];
        if (Array.isArray(loopData)) {
          return loopData.flatMap((loopItem) => {
            return element.elements.flatMap((loopElement) => {
              const newElement = JSON.parse(JSON.stringify(loopElement));
              if (newElement.type === "text" && newElement.content && typeof newElement.content === "string") {
                newElement.content = replacePlaceholders(newElement.content, loopItem);
              }
              return newElement;
            });
          });
        }
        return [];
      }
      return element;
    });
    return processedTemplate;
  };

  const evaluateCondition = (condition, data) => {
    try {
      const resolvedCondition = condition.replace(/{{([^}]+)}}/g, (match, placeholder) => {
        const value = getDeepValue(data, placeholder);
        return value !== undefined ? JSON.stringify(value) : "undefined";
      });
      return eval(resolvedCondition);
    } catch (error) {
      console.error("Error evaluating condition:", condition, error);
      return false;
    }
  };

  const replacePlaceholders = (text, data) => {
    return text.replace(/{{([^}]+)}}/g, (match, placeholder) => {
      const value = getDeepValue(data, placeholder);
      return value !== undefined ? value : "";
    });
  };

  const getDeepValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
  };

  const printToken = (receiptData, estimateId, documentNo) => {
    const tillData = JSON.parse(localStorage.getItem("tillData"));
    const printerURL = tillData.tillAccess.cwrTill.hardwareController.imageUrl;
    const productSkuValue = receiptData?.items?.map((item) => {
      if (item?.uomData?.[0]?.decimal === "Y") {
        return (
          5 +
          item?.value.toString().padStart(6, "0").slice(0, 6) +
          Math.round(item?.weight * 10000)
            .toString()
            .padStart(6, "0")
        );
      } else {
        return (
          8 +
          item?.value.toString().padStart(6, "0").slice(0, 6) +
          Math.round(item?.weight * 10)
            .toString()
            .padStart(6, "0")
        );
      }
    });

    const barcode = estimateId ? estimateId : productSkuValue.join(" ");
    const printData = {
      productionCenter: "Estimate",
      saleType: receiptData?.saleType?.name || "Walk-in",
      estimateNo: documentNo || "",
      salesRepName: receiptData?.salesRepName || "",
      orderDate: receiptData?.orderDate || new Date().toLocaleString(),
      documentno: receiptData?.documentno,
      customer: receiptData?.customer?.firstName,
      length: receiptData?.items?.length || 0,
      total: receiptData?.total || 0,
      items:
        receiptData?.items?.map((item) => ({
          name: item.name,
          ediCode:
          Array.isArray(item.uomData) && item.uomData.length > 0
            ? item.uomData[0]?.ediCode || ""
            : "",
          weight: item.weight,
          linestd: item.linestd,
        })) || [],
      qrcode: barcode, // Pass the formatted string
    };
    console.log("========>", printData)
    // Process template
    const processedToken = processTokenTemplate(JSON.parse(tillData.tillAccess.cwrTill.kotPrintTemplate.xmlcode2), printData);

    // Update QR code content in the processed template
    processedToken.elements = processedToken.elements.map((element) => {
      if (element.type === "qrcode") {
        return {
          ...element,
          content: barcode, // Set QR code content
        };
      }
      return element;
    });

    Axios.post(`${printerURL}printer`, JSON.stringify(processedToken), {
      headers: { "Content-Type": "application/json" }, // Recommended content type
    })
      .then((response) => console.log("Print success:", response))
      .catch((error) => console.error("Print error:", error));
  };
  if (tillData?.tillAccess?.cwrTill?.kotPrintTemplate?.xmlcode2) {
    // Call printToken for each vendor with the same bill token
    printToken(receiptData, estimateId, documentNo);
  }

  return receiptData;
};

const formatValue = (value, format, column) => {
  let formatted = value;

  if (format && format === "currency" && typeof value === "number") {
    formatted = value.toFixed(2);
  }

  if (column?.trim && typeof formatted === "string" && column?.width) {
    formatted = formatted.slice(0, column.width);
  }

  return formatted;
};

export default CWEstimatePrinter;
